import { useState, useEffect, useCallback } from "react";
import { useAdminApi, toLocalDateStr, exportXLSX, fmtMoney } from "../../adminUtils";
import ExportBar from "./ExportBar";

const fmtDay = d => d
  ? new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
  : "—";

const fmtSpan = (a, b) => (!a ? "" : a === b ? fmtDay(a) : `${fmtDay(a)} – ${fmtDay(b)}`);

export default function WorkshopRevenueReport() {
  const { API, AUTH } = useAdminApi();
  const today = toLocalDateStr(new Date());
  const [from, setFrom]       = useState(() => { const d = new Date(); d.setDate(1); return toLocalDateStr(d); });
  const [to, setTo]           = useState(today);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const fetch_ = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await fetch(`${API}/reports/workshop-revenue?from=${from}&to=${to}`, { headers: AUTH });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Could not load report.");
      setData(json);
    } catch (e) { setData(null); setError(e.message); }
    finally     { setLoading(false); }
  }, [from, to, API, AUTH]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const doExport = () => {
    if (!data?.workshops) return;
    const rows = [];
    for (const w of data.workshops) {
      for (const s of w.sessions) {
        rows.push({
          Workshop: w.title, Teacher: w.teacher, Date: s.date, Session: s.label, Time: s.time,
          Bookings: s.bookings, Comp: s.comp, Revenue: s.revenue,
        });
      }
      rows.push({ Workshop: w.title, Teacher: w.teacher, Session: "Workshop total", Bookings: w.bookings, Revenue: w.revenue });
      rows.push({ Workshop: w.title, Session: `Teacher pay (${w.teacherSplit}%)`, Revenue: w.teacherPay });
      rows.push({ Workshop: w.title, Session: "Studio take", Revenue: w.studioTake });
    }
    rows.push({ Workshop: "ALL WORKSHOPS", Bookings: data.grandTotal.bookings, Revenue: data.grandTotal.revenue });
    exportXLSX("Workshop Revenue", rows, `workshop-revenue-${from}-${to}.xlsx`);
  };

  const g = data?.grandTotal;

  return (
    <div>
      <div className="adm-report-filters">
        <div className="adm-filter-group">
          <label className="adm-filter-label">From</label>
          <input type="date" className="adm-input" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="adm-filter-group">
          <label className="adm-filter-label">To</label>
          <input type="date" className="adm-input" value={to} onChange={e => setTo(e.target.value)} />
        </div>
      </div>
      {loading && <p className="adm-loading">Loading…</p>}
      {error && !loading && <p className="adm-error">{error}</p>}
      {data && !loading && (
        <>
          <ExportBar onXLSX={doExport} />
          {data.workshops.length === 0
            ? <p className="adm-empty">No workshop bookings in this period.</p>
            : <>
                <div className="adm-report-summary-row">
                  <div className="adm-summary-chip adm-summary-chip--green">
                    <span className="adm-summary-val">{fmtMoney(g.revenue)}</span>
                    <span className="adm-summary-lbl">Revenue</span>
                  </div>
                  <div className="adm-summary-chip">
                    <span className="adm-summary-val">{fmtMoney(g.teacherPay)}</span>
                    <span className="adm-summary-lbl">Teacher pay</span>
                  </div>
                  <div className="adm-summary-chip adm-summary-chip--green">
                    <span className="adm-summary-val">{fmtMoney(g.studioTake)}</span>
                    <span className="adm-summary-lbl">Studio take</span>
                  </div>
                  <div className="adm-summary-chip">
                    <span className="adm-summary-val">{data.workshops.length}</span>
                    <span className="adm-summary-lbl">Workshops</span>
                  </div>
                  <div className="adm-summary-chip">
                    <span className="adm-summary-val">{g.bookings}</span>
                    <span className="adm-summary-lbl">Session bookings</span>
                  </div>
                </div>

                {data.workshops.map(w => (
                  <div key={w.workshopId} className="adm-report-teacher">
                    <div className="adm-report-teacher-header">
                      <span className="adm-report-teacher-name">{w.title}</span>
                      <span className="adm-report-teacher-rate">
                        {[w.teacher, fmtSpan(w.firstDate, w.lastDate),
                          `${w.participants} participant${w.participants !== 1 ? "s" : ""}`]
                          .filter(Boolean).join(" · ")}
                      </span>
                    </div>
                    <table className="adm-report-table">
                      <thead>
                        <tr><th>Date</th><th>Session</th><th>Time</th><th>Bookings</th><th>Revenue</th></tr>
                      </thead>
                      <tbody>
                        {w.sessions.map((s, j) => (
                          <tr key={j}>
                            <td>{fmtDay(s.date)}</td>
                            <td>{s.label}</td>
                            <td>{s.time || "—"}</td>
                            <td>{s.bookings}{s.comp > 0 && <span className="adm-muted"> ({s.comp} comp)</span>}</td>
                            <td>{fmtMoney(s.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="adm-report-subtotal">
                          <td colSpan={3}>Workshop total</td><td>{w.bookings}</td><td>{fmtMoney(w.revenue)}</td>
                        </tr>
                        <tr>
                          <td colSpan={4}>Teacher pay ({w.teacherSplit}%)</td><td>{fmtMoney(w.teacherPay)}</td>
                        </tr>
                        <tr>
                          <td colSpan={4}>Studio take</td><td>{fmtMoney(w.studioTake)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ))}
              </>
          }
        </>
      )}
    </div>
  );
}
